/**
 * Status pill — a centred, oval MUI Chip that represents a projected request event
 * inline in the chat timeline.
 *
 * Displayed for events such as "Anfrage veröffentlicht", "Zuschlag erteilt", etc.
 */

import React from "react";
import {Box, Chip, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {NotificationI18N} from "@/translations";
import {isClientRole, useAuth} from "@/app/auth/AuthContext";
import {formatIsoDate, formatIsoDateTime} from "@/lib/utils";
import {OrderStatus} from "@/api/data-contracts";
import type {StatusPillDTO} from "./types";

interface StatusPillProps {
    pill: StatusPillDTO;
}

/**
 * Resolves the localised label key for a REQUEST_CONFIRMED pill.
 *
 * Three branches:
 *  - actorIsCurrentCompany === true AND isRequesterView === true (buyer) → buyer self wording
 *  - actorIsCurrentCompany === true AND isRequesterView === false (supplier winner) → winner self wording
 *  - actorIsCurrentCompany !== true → other wording (neutral, no name leak)
 */
function resolveRequestConfirmedKey(
    actorIsCurrentCompany: boolean | null | undefined,
    isRequesterView: boolean
): string {
    if (actorIsCurrentCompany === true) {
        return isRequesterView
                ? NotificationI18N.chat.statusPill.requestConfirmedBuyerSelf
                : NotificationI18N.chat.statusPill.requestConfirmedWinnerSelf;
    }
    return NotificationI18N.chat.statusPill.requestConfirmedOther;
}

/**
 * Resolves the localised label key for a QUOTE_DISMISSED pill.
 *
 * Three branches:
 *  - isRequesterView === true → requester actively dismissed (buyer self wording)
 *  - isRequesterView === false AND actor === true → loser supplier self wording
 *  - otherwise → other wording
 */
function resolveQuoteDismissedKey(
        actor: boolean | null | undefined,
        isRequesterView: boolean
): string {
    if (isRequesterView) {
        return NotificationI18N.chat.statusPill.quoteDismissedRequesterSelf;
    }
    // loser = supplier whose quote was not chosen
    if (actor === true) {
        return NotificationI18N.chat.statusPill.quoteDismissedLoserSelf;
    }
    return NotificationI18N.chat.statusPill.quoteDismissedOther;
}

/**
 * Selects the self or other translation key based on actor perspective.
 */
function perspectiveKey(
        actor: boolean | null | undefined,
        selfKey: string,
        otherKey: string
): string {
    return actor === true ? selfKey : otherKey;
}

/**
 * Resolves the localised label key for an ORDER_STATUS_CHANGED pill based on the
 * targetOrderStatus carried by the event. Falls back to the generic
 * orderStatusChanged wording for historical events without targetOrderStatus.
 */
function resolveOrderStatusChangedKey(
        actor: boolean | null | undefined,
        targetOrderStatus: OrderStatus | null | undefined
): string {
    const sp = NotificationI18N.chat.statusPill;
    switch (targetOrderStatus) {
        case OrderStatus.ORDER_CONFIRMED:
            return perspectiveKey(actor, sp.orderConfirmedSelf, sp.orderConfirmedOther);
        case OrderStatus.DELIVERY_DATE_PROPOSED:
            return perspectiveKey(actor, sp.orderDeliveryDateProposedSelf, sp.orderDeliveryDateProposedOther);
        case OrderStatus.DELIVERY_DATE_CONFIRMED:
            return perspectiveKey(actor, sp.orderDeliveryDateConfirmedSelf, sp.orderDeliveryDateConfirmedOther);
        case OrderStatus.DELIVERED:
            return perspectiveKey(actor, sp.orderDeliveredSelf, sp.orderDeliveredOther);
        default:
            return perspectiveKey(actor, sp.orderStatusChangedSelf, sp.orderStatusChangedOther);
    }
}

/**
 * Resolves the localised label key for an ORDER_CANCELLED pill. DEACTIVATED is shown
 * via a neutral key (actor ignored) — the backend fallback in resolveEffectiveActor
 * sets a supplier-actor flag for auto-deactivations that is not semantically meaningful
 * here. Historical events without targetOrderStatus fall back to the generic wording.
 */
function resolveOrderCancelledKey(
        actor: boolean | null | undefined,
        targetOrderStatus: OrderStatus | null | undefined
): string {
    const sp = NotificationI18N.chat.statusPill;
    switch (targetOrderStatus) {
        case OrderStatus.CANCELLED_BY_SUPPLIER:
            return perspectiveKey(actor, sp.orderCancelledBySupplierSelf, sp.orderCancelledBySupplierOther);
        case OrderStatus.CANCELLED_BY_REQUESTER:
            return perspectiveKey(actor, sp.orderCancelledByRequesterSelf, sp.orderCancelledByRequesterOther);
        case OrderStatus.DEACTIVATED:
            return sp.orderDeactivated;
        default:
            return perspectiveKey(actor, sp.orderCancelledSelf, sp.orderCancelledOther);
    }
}

/**
 * Maps the backend `kind` string to a localised translation key using tri-state
 * `actorIsCurrentCompany` perspective logic.
 *
 * - actorIsCurrentCompany === null  → neutral key (no _SELF / _OTHER suffix)
 * - actorIsCurrentCompany === true  → _SELF key (viewer performed the action)
 * - actorIsCurrentCompany === false → _OTHER key (another company performed the action)
 *
 * REQUEST_CONFIRMED and QUOTE_DISMISSED have special handling that further branches
 * by `viewerIsRequester`. The `isBuyer` parameter serves as a fallback heuristic for
 * historical pills where `viewerIsRequester` is not yet populated (old backends, test doubles).
 */
function resolvePillKey(
    kind: string | undefined,
    actor: boolean | null | undefined,
    isBuyer: boolean,
    viewerIsRequester: boolean | null | undefined,
    targetOrderStatus: OrderStatus | null | undefined
): string {
    const isRequesterView = viewerIsRequester ?? isBuyer;
    const sp = NotificationI18N.chat.statusPill;
    switch (kind) {
        case "REQUEST_PUBLISHED":
            return perspectiveKey(actor, sp.requestPublishedSelf, sp.requestPublishedOther);
        case "REQUEST_UNPUBLISHED":
            return sp.requestUnpublished;
        case "REQUEST_CANCELLED":
            return sp.requestCancelled;
        case "REQUEST_DEACTIVATED":
            return sp.requestDeactivated;
        case "REQUEST_DATES_EXTENDED":
            return sp.requestDatesExtended;
        case "REQUEST_ADDITIONAL_INFORMATION_UPDATED":
            return perspectiveKey(actor, sp.requestAdditionalInfoUpdatedSelf, sp.requestAdditionalInfoUpdatedOther);
        case "QUOTE_SUBMITTED":
            return perspectiveKey(actor, sp.quoteSubmittedSelf, sp.quoteSubmittedOther);
        case "REVISED_QUOTE_SUBMITTED":
            return perspectiveKey(actor, sp.revisedQuoteSubmittedSelf, sp.revisedQuoteSubmittedOther);
        case "QUOTE_REJECTED":
            return perspectiveKey(actor, sp.quoteRejectedSelf, sp.quoteRejectedOther);
        case "QUOTE_DISMISSED":
            return resolveQuoteDismissedKey(actor, isRequesterView);
        case "QUOTE_FEEDBACK_GIVEN":
            return perspectiveKey(actor, sp.quoteNegotiationStartedSelf, sp.quoteNegotiationStartedOther);
        case "QUOTE_WITHDRAWN":
            return perspectiveKey(actor, sp.quoteWithdrawnSelf, sp.quoteWithdrawnOther);
        case "REQUEST_CONFIRMED":
            return resolveRequestConfirmedKey(actor, isRequesterView);
        case "ORDER_STATUS_CHANGED":
            return resolveOrderStatusChangedKey(actor, targetOrderStatus);
        case "ORDER_CANCELLED":
            return resolveOrderCancelledKey(actor, targetOrderStatus);
            // ORDER_DELIVERY_DATE_REJECTED is a distinct RequestEventType on the backend, NOT an Order.status value
            // (the order reverts to ORDER_CONFIRMED, not to a DELIVERY_DATE_REJECTED status), so it cannot be
            // handled inside resolveOrderStatusChangedKey and must be resolved here as its own top-level kind.
        case "ORDER_DELIVERY_DATE_REJECTED":
            return perspectiveKey(actor, sp.orderDeliveryDateRejectedSelf, sp.orderDeliveryDateRejectedOther);
            // SUPPLIER_VIEWED is a synthetic, frontend-only pill kind (not a backend RequestEventType) —
            // it is built by ChatWindow from `chat.supplierViewedAt`. Rendered role-neutral (no _SELF/
            // _OTHER perspective) since the backend already restricts `supplierViewedAt` to the buyer/
            // operator view — the supplier never sees this pill.
        case "SUPPLIER_VIEWED":
            return sp.supplierViewed;
        default:
            return kind ?? "";
    }
}

/**
 * Returns true when resolvePillKey could not find a known translation key for the given
 * event kind. resolvePillKey signals this by returning the raw kind string itself (for
 * unknown kinds) or an empty string (when kind is undefined). In both cases calling t()
 * would produce a misleading result, so pillLabel skips the translation step.
 */
function isUnresolvedTranslationKey(key: string, kind: string | undefined): boolean {
    return key === kind || key === "";
}

/**
 * Resolves a translated label for a status pill.
 * Falls back to the raw kind string for unknown event types.
 *
 * Legacy events written before `source_company_id` was introduced (May 2026) carry
 * `companyName = null`. For those, the i18next `context: "noName"` selects a fallback
 * translation key (e.g. `quoteSubmittedOther_noName`) that phrases the message without
 * the company name. When no `_noName` variant exists, i18next falls back to the base key
 * — there the empty `companyName` would interpolate as an empty string, so any template
 * that uses `{{companyName}}` must have a matching `_noName` key (in `src/translations.ts` and the
 * runtime text store).
 */
function pillLabel(
        kind: string | undefined,
        pill: StatusPillDTO,
        isBuyer: boolean,
        t: ReturnType<typeof useTranslation>["t"],
        locale: string
): string {
    const translationKey = resolvePillKey(
            kind,
            pill.actorIsCurrentCompany,
            isBuyer,
            pill.viewerIsRequester,
            pill.targetOrderStatus
    );
    if (isUnresolvedTranslationKey(translationKey, kind)) {
        return translationKey;
    }
    const hasCompanyName = !!pill.companyName && pill.companyName.trim().length > 0;
    return t(translationKey, {
        context: hasCompanyName ? undefined : "noName",
        boxhubNumber: pill.boxhubNumber,
        companyName: pill.companyName,
        deliveryDate: formatIsoDate(pill.deliveryDate, locale)
    });
}

const StatusPill: React.FC<StatusPillProps> = ({pill}) => {
    const {t, i18n} = useTranslation();
    const { roles } = useAuth();
    const isBuyer = isClientRole(roles);
    const label = pillLabel(pill.kind, pill, isBuyer, t, i18n.language);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                my: 1,
            }}
        >
            <Box sx={{display: "inline-flex", flexDirection: "column", alignItems: "flex-end"}}>
                <Chip
                        size="small"
                        label={label}
                        sx={{
                            borderRadius: "50px",
                            bgcolor: "grey.200",
                            color: "text.secondary",
                            fontSize: "0.75rem",
                            fontStyle: "italic",
                        }}
                />
                {pill.timestamp && (
                        <Typography
                                variant="caption"
                                sx={{color: "text.secondary", fontStyle: "italic"}}
                        >
                            {formatIsoDateTime(pill.timestamp, i18n.language)}
                        </Typography>
                )}
            </Box>
        </Box>
    );
};

export default StatusPill;
